import { Briefcase, Store } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BUSINESS_VERTICALS, type BusinessVertical } from "@graftus/schema";
import { StepHeading } from "../OnboardingShell";
import { StepFooter } from "../StepFooter";
import { ChoiceCard } from "../ui/ChoiceCard";

const ICONS: Record<BusinessVertical, typeof Store> = { dvh: Store, sales: Briefcase };
/** Grocery first: it is the pilot vertical and the prototype's first card. */
const ORDER: BusinessVertical[] = ["dvh", "sales"];

interface SectorStepProps {
  value: BusinessVertical | null;
  onChange: (sector: BusinessVertical) => void;
  onNext: () => void;
  onBack?: () => void;
}

/** Step 2: which kind of organisation, which decides every sector word after it. */
export function SectorStep({ value, onChange, onNext, onBack }: SectorStepProps) {
  const { t } = useTranslation("onboarding");
  return (
    <div data-testid="onboarding-sector">
      <StepHeading title={t("sector.title")} lead={t("sector.lead")} />
      <div role="radiogroup" aria-label={t("sector.title")} className="flex flex-col gap-3">
        {ORDER.filter((key) => BUSINESS_VERTICALS.includes(key)).map((key) => {
          const Icon = ICONS[key];
          return (
            <ChoiceCard
              key={key}
              selected={value === key}
              onSelect={() => onChange(key)}
              icon={<Icon className="h-5 w-5" />}
              title={t(`sector.cards.${key}.title`)}
              description={t(`sector.cards.${key}.desc`)}
              meta={t(`sector.cards.${key}.kpis`)}
            />
          );
        })}
      </div>
      {value ? (
        <p
          className="mt-4 rounded-g-md bg-g-primary-wash px-4 py-3 text-[14px] leading-relaxed text-g-ink-700 animate-in fade-in duration-200"
          data-testid="onboarding-sector-preview"
        >
          {t(`sector.preview.${value}`)}
        </p>
      ) : null}
      <StepFooter
        primaryLabel={t("sector.next")}
        onPrimary={onNext}
        primaryDisabled={!value}
        onBack={onBack}
      />
    </div>
  );
}
